import { config } from './config.js';
import { getGenotypeDisplayParts } from 'src/util/util';
import { sortLabelValues } from './labelValueSort';
import { filterLabelValues, overflowRowText } from './labelValueFilter';
import { consultationRowRuns } from './consultationDates';
import {
  getCarrierStatusLabel,
  isUnrecognisedCarrierStatus
} from 'src/components/Constants';

// Shared, d3-FREE label text helpers.
//
// Long node labels (Tests / Diagnosis / Consultation Date / Test Comment) once
// rendered as one comma-joined horizontal line that overflowed into
// neighbouring nodes. They became a HEADER line plus one word-wrapped line per
// item (TGCLOUD-1610); the wrapping half of that is now gone again, because a
// record broken across rows reads as a paragraph rather than a list
// (TGCLOUD-1781). The shape is: a header row, then exactly ONE ROW PER RECORD,
// never wrapped. The Person Note is the only label that still wraps.
//
// Whatever the shape, the same pure function produces it for getLabelPos (which
// reserves the vertical space) AND for the Canvas renderer (which draws it), so
// the two can never disagree on a block's row count — that is what guarantees
// stacked labels do not overlap, and a sort or filter applied in only one of
// them is the drift that painted labels at y=0 (TGCLOUD-1610).
//
// The Person Note's wrap is still a CHAR estimate here rather than a pixel
// measurement, because getLabelPos runs before anything is in the DOM. The
// renderer wraps the same note by real pixels (`wrap()` in utils.js); they agree
// only because AVG_GLYPH_W below is deliberately a touch HIGH, so the estimate
// over-counts rather than under-reserves.

// Conservative average glyph width as a fraction of font size. Slightly high so
// estimated lines are a touch SHORTER than the pixel budget → never overflow.
const AVG_GLYPH_W = 0.6;

// Vertical pitch between stacked label rows, as a multiple of font size.
//
// SINGLE SOURCE OF TRUTH (TGCLOUD-1610). Reservation (computeLabelLayout) used
// 1.35 while the renderer stepped its multi-row blocks — external IDs,
// diagnoses, phenotypes, genotypes — by 1.4, so an N-row block drifted
// N * 0.05 * fontSize BELOW its reserved slot and ate into the block under it.
// Both now read this constant, so a row lands exactly where space was kept for
// it. (1.35 rather than 1.4: matching upward would have inflated every reserved
// block, and with it every auto LEVEL_HEIGHT, for no visual gain.)
export const LABEL_ROW_PITCH = 1.35;

// Approx number of characters that fit on one label line at a GIVEN node gap /
// symbol size / font — config-free so auto-spacing can ask "how many chars fit
// if the gap were X?" without mutating the live config (it computes a gap and
// must wrap at that candidate gap, not the current one).
export function labelMaxCharsAt(nodeGapPx, symbolSize, fontSize) {
  const sym = Number(symbolSize) || 45;
  const gap = Number(nodeGapPx) || 800;
  const fs = Number(fontSize) || 16;
  const avail = Math.max(gap - sym, fs); // never below ~one glyph
  return Math.max(1, Math.floor(avail / (fs * AVG_GLYPH_W)));
}

// Approx number of characters that fit on one label line at the current font,
// within the gap between a node's column and its neighbour's.
export function labelMaxChars(options) {
  return labelMaxCharsAt(
    config.NODE_GAP,
    config.SYMBOL_SIZE,
    options?.font_size
  );
}

// Greedy word-wrap `text` to lines of at most `maxChars` characters. A single
// token longer than a line is hard-broken so it can never overflow. Returns []
// for empty input.
export function wrapLabelText(text, maxChars) {
  const str = String(text ?? '').trim();
  if (!str) return [];
  const max = Math.max(1, Math.floor(maxChars) || 1);
  const lines = [];
  let line = '';
  for (let word of str.split(/\s+/)) {
    // Hard-break a token that can't fit on a line by itself.
    while (word.length > max) {
      if (line) {
        lines.push(line);
        line = '';
      }
      lines.push(word.slice(0, max));
      word = word.slice(max);
    }
    if (!word) continue;
    if (!line) line = word;
    else if (line.length + 1 + word.length <= max) line += ' ' + word;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// Normalise a person's field to a clean array of non-empty item strings.
export function labelBlockItems(d, field) {
  const v = d?.[field];
  if (v == null) return [];
  const arr = Array.isArray(v) ? v : [v];
  return arr.map((x) => String(x ?? '').trim()).filter(Boolean);
}

// ONE ROW PER RECORD (TGCLOUD-1781).
//
// These blocks used to word-wrap every item, so one long test name or a run of
// consultation dates became several rows of broken text. Kathryn's rule is that
// a clinical record occupies exactly one row and only the Person Note may wrap:
// a wrapped list of records reads as a paragraph, and a clinician scanning a
// chart counts rows.
//
// `wrapLabelText` itself is deliberately untouched — the Person Note still needs
// it, and `estimateNoteLines` depends on `AVG_GLYPH_W` being a touch HIGH so its
// character estimate over-counts against the renderer's pixel wrap. Making
// records fit by lowering that constant would silently flip the note into
// under-reserving and overlap whatever sits below it.
//
// The cost is horizontal: a row wider than the gap now overflows towards the
// neighbour instead of wrapping. Auto-spacing covers that — these blocks move
// from its capped `wrappable` bucket to the uncapped `singleLine` one, so the
// node gap grows to fit them (autoSpacing.js). With auto-spacing off, a very
// long single record overflows, which is the behaviour this label had before
// TGCLOUD-1610 and is the trade the ticket asks for.
//
// getLabelPos reserves `lines.length` rows and the renderer draws exactly these
// lines, so both follow automatically.

// A label row's text, as the single line it will be drawn on. Empty input
// yields no row at all, so a blank record never reserves vertical space.
const singleLineOf = (text) => {
  const str = String(text ?? '').trim();
  return str ? [str] : [];
};

// The full ordered list of rendered lines for a label block: the header row
// followed by exactly one row per item.
export function buildLabelBlockLines(headerText, items) {
  const lines = [];
  if (headerText) lines.push(...singleLineOf(headerText));
  for (const item of items) lines.push(...singleLineOf(item));
  return lines;
}

// Vertical clearance (px below the node centre) the FIRST label must start at so
// it never collides with a fertility / infertility mark — or 0 when the person
// has none. Two mark shapes, both keyed off SYMBOL_SIZE so they scale with zoom:
//   • a SPOUSELESS person's own mark hangs straight below the node — fertile bar
//     at 0.85*sym, infertile lower bar at 1.05*sym (Canvas single-node render);
//   • a COUPLE's mark hangs from the relationship line at the union midpoint and
//     drops further — fertile bar at 1.4*sym, infertile lower bar at 1.65*sym
//     (Canvas `couple-fertility` path: drop = 1.4*sym, gap = 0.25*sym).
// A tightly-spaced couple puts that mark right where a partner's first label
// (e.g. a gender-identity acronym) sits, so the baseline must clear the DEEPEST
// applicable bar by a text ascent (~0.8em) plus a small gap. Pure + d3-free so
// getLabelPos can reserve exactly what the Canvas renderer will draw.
export function fertilityLabelClearance(d, sym, fs) {
  let markBottom = 0;

  const spouseless = !d?.spouseIds || d.spouseIds.length === 0;
  if (d?.fertility && spouseless) {
    markBottom = (d.fertility === 'infertile' ? 1.05 : 0.85) * sym;
  }

  const coupleFerts = d?.spouseRelationships
    ? Object.values(d.spouseRelationships)
        .map((r) => r && r.fertility)
        .filter(Boolean)
    : [];
  if (coupleFerts.length) {
    const coupleBottom = (coupleFerts.includes('infertile') ? 1.65 : 1.4) * sym;
    markBottom = Math.max(markBottom, coupleBottom);
  }

  if (!markBottom) return 0;
  const ascent = fs * 0.8; // text rises ~0.8em above its baseline
  const gap = sym * 0.2;
  return markBottom + ascent + gap;
}

// Compose each test record into styled SEGMENTS for its main line plus its
// comment. Each segment is { text, key } where `key` is the label toggle that
// styles it — so Test Name / Genes Tested / Test Result / Variants can be
// formatted INDEPENDENTLY; literal separators carry key:null (unstyled). The
// main line reads "<name> (<genes>, <result>, <variants>)" — bracket dropped
// when no inner segment shows, inner segments shown alone (no empty bracket)
// when the name toggle is off. `comment` is the test's own note (gated by the
// Test Comments toggle), rendered italic on its own line. Multiple genes/
// variants are comma-joined; a variant is the three HGVS fields pre-joined with
// " - " by the backend.
const composeTestRecord = (t, ls) => {
  const inner = [];
  if (ls?.genesTested && t.genes && t.genes.length) {
    inner.push({ text: t.genes.join(', '), key: 'genesTested' });
  }
  if (ls?.testResults && t.result) {
    inner.push({ text: t.result, key: 'testResults' });
  }
  if (ls?.variants && t.variants && t.variants.length) {
    inner.push({ text: t.variants.join(', '), key: 'variants' });
  }
  const name = ls?.tests && t.name ? String(t.name).trim() : '';
  const segments = [];
  if (name) {
    segments.push({ text: name, key: 'tests' });
    if (inner.length) {
      segments.push({ text: ' (', key: null });
      inner.forEach((seg, i) => {
        if (i) segments.push({ text: ', ', key: null });
        segments.push(seg);
      });
      segments.push({ text: ')', key: null });
    }
  } else {
    inner.forEach((seg, i) => {
      if (i) segments.push({ text: ', ', key: null });
      segments.push(seg);
    });
  }
  const comment = ls?.pedigreeText && t.comment ? String(t.comment).trim() : '';
  return { segments, comment };
};

/**
 * The Tests block's own VISIBLE → FILTER → SORT → LIMIT pipeline, returning
 * `{ items, hiddenCount }` — the same contract `selectDiagnosesRows` and its
 * siblings in utils.js return.
 *
 * TGCLOUD-1792 offered `limit` and `within` for the `tests` unit, the modal
 * rendered the controls and the active-filter badge, and the preset stored the
 * values — but `filterLabelValues` had exactly ONE production caller
 * (`selectRecords` in utils.js), reached only for genotypes, phenotypes and
 * diagnoses. The Tests block was built straight off `testRecords`, so a preset
 * with a tests limit of 3 against a person with 11 tests still drew all 11 rows
 * and no overflow line. This routes it through the same path rather than
 * inventing a second one.
 *
 * Order matters and mirrors `selectRecords` exactly:
 *   VISIBLE — a record that composes to no segments AND no comment draws no row
 *             (every one of its field toggles is off, or its fields are empty),
 *             so it must not consume one of the limit's slots. Folding this in
 *             first is what makes a cap of 3 mean three drawn rows; applied
 *             after the cap it would silently draw fewer.
 *   FILTER + SORT + LIMIT — `sortLabelValues` then `filterLabelValues`, which
 *             filters and caps in that fixed order so "date desc, at most 1"
 *             means "the most recent".
 *
 * `hiddenCount` counts withheld RECORDS, not withheld rows: a test with a
 * comment occupies two rows but is one record, and "+ 2 more" has to read as
 * two more tests.
 */
export function selectTestRecords(d, ls) {
  const records = Array.isArray(d?.testRecords) ? d.testRecords : [];
  const visible = records.filter((t) => {
    const it = composeTestRecord(t, ls);
    return it.segments.length > 0 || it.comment !== '';
  });
  // The single place per-test row order is decided (TGCLOUD-1663). buildTestItems
  // and buildTestBlockLines both route through here, and so do the renderer, the
  // row reservation and auto-spacing's measurement — so sorting here means all
  // of them see the same sequence and cannot drift.
  return filterLabelValues('tests', sortLabelValues('tests', visible, ls), ls);
}

export function buildTestSegments(d, ls) {
  return selectTestRecords(d, ls).items.map((t) => composeTestRecord(t, ls));
}

// Flat { main, comment } view (segments concatenated) — kept for callers/tests
// that want the composed string rather than styled runs.
export function buildTestItems(d, ls) {
  return buildTestSegments(d, ls).map((it) => ({
    main: it.segments
      .map((s) => s.text)
      .join('')
      .trim(),
    comment: it.comment
  }));
}

// One genotype row as independently-styleable runs — "BRCA1 (NCBIGene:672) -
// Heterozygote". Symbol / ID / carrier status are independent toggles: the
// " - " separator only appears when something precedes the status, and the
// status shows alone when both other toggles are off.
//
// SINGLE SOURCE OF TRUTH for the row (TGCLOUD-1610). The renderer used to build
// these runs inline while auto-spacing measured symbol + (id) only and
// getLabelPos gated the row on those same two toggles — so the carrier status
// was drawn but never measured (clipped labels) and a carrier-status-ONLY
// configuration reserved no vertical space at all, painting the row at y=0 on
// top of the symbol. Renderer, width measurement and height reservation now all
// read this one function, so they cannot drift again.
//
// A value we cannot interpret still renders verbatim, but derives no +/- marker
// — a gap that is otherwise invisible, because the label looks perfectly
// correct. Italic locates it on the chart; the family-card notice says what the
// italics mean (TGCLOUD-1647).
//
// Note: a diagnoses-sourced gene row (collectGenotypeSource) carries no
// carrierStatus, so it never produces a status run — deliberate, unchanged.
export function buildGenotypeRuns(g, ls) {
  if (!g) return [];
  const parts = getGenotypeDisplayParts(g);
  const symbol = ls?.genotypesName && parts.symbol ? parts.symbol : '';
  const idPart = ls?.genotypesID ? `(${g.id})` : '';
  const runs = [];
  if (symbol) runs.push({ text: symbol, key: 'genotypesName' });
  if (symbol && idPart) runs.push({ text: ' ', key: null });
  if (idPart) runs.push({ text: idPart, key: 'genotypesID' });
  if (ls?.genotypeCarrierStatus) {
    const carrierLabel = getCarrierStatusLabel(g.carrierStatus);
    if (carrierLabel) {
      if (runs.length) runs.push({ text: ' - ', key: null });
      runs.push({
        text: carrierLabel,
        key: 'genotypeCarrierStatus',
        italic: isUnrecognisedCarrierStatus(g.carrierStatus)
      });
    }
  }
  return runs;
}

// The composed text of a genotype row — what auto-spacing measures, so the
// measured string is character-for-character what the renderer draws.
export function genotypeRowText(g, ls) {
  return buildGenotypeRuns(g, ls)
    .map((r) => r.text)
    .join('');
}

// Estimated rendered line count for a free-text Person Note. The renderer
// pixel-wraps the note into N tspans while reservation used to allow exactly
// ONE row, so a multi-line note under-reported the node's block height. That
// slack was hidden by the old global LEVEL_HEIGHT; per-generation spacing
// (TGCLOUD-1610) removes the slack, so the estimate has to be honest.
//
// Uses the same conservative char estimate as every other block — AVG_GLYPH_W
// is deliberately a touch high, so this over-counts rather than under-counts
// against the pixel wrap. Explicit newlines start a new row (blank lines
// included), matching the renderer's per-source-line wrap.
export function estimateNoteLines(note, maxChars) {
  const str = String(note ?? '');
  if (!str.trim()) return 0;
  return str
    .split(/\r?\n/)
    .reduce(
      (n, line) => n + Math.max(1, wrapLabelText(line, maxChars).length),
      0
    );
}

// `wrapSegments` lived here — a run-aware word wrap that split one test's
// styled segments across several rows. It had no caller left once a test became
// one row (TGCLOUD-1781) and nothing outside this module ever used it, so it is
// gone rather than left as a dead wrapper in the module whose contract is now
// "records do not wrap". `wrapLabelText` stays: the Person Note still wraps, and
// `estimateNoteLines` reserves its rows with it.

// The "+ N more" row, as one more line in a stacked block's line list — or null
// when the filter withheld nothing.
//
// Shaped as a line rather than as a separate draw call on purpose. Every
// consumer of a stacked block already counts `blockLines(...).length` to reserve
// rows and iterates the same array to draw them, so an appended line is reserved
// and drawn by the code that is already there; a parallel "and also draw this"
// channel is exactly how the reservation and the renderer drift apart
// (TGCLOUD-1610). `muted` asks the renderer for the same reduced-opacity italic
// treatment `drawOverflowRow` gives the diagnosis / phenotype / genotype
// overflow rows, so the row reads as a note about the list rather than as
// another record in it.
const overflowBlockLine = (hiddenCount) => {
  const text = overflowRowText(hiddenCount);
  return text
    ? { runs: [{ text, key: null }], italic: true, muted: true }
    : null;
};

// Wrapped, header-prefixed lines for the composed Tests block. Returns
// { runs:[{text,key}], italic } objects: the "Tests:" header is unstyled; each
// test's main wraps into per-field styled runs; the test's comment follows on
// its own italic line (keyed `pedigreeText` so the Test Comments toggle can also
// bold it). Empty when no test contributes anything. Used for BOTH layout
// reservation (line count) and rendering, so they stay in lockstep.
export function buildTestBlockLines(d, ls) {
  const { items: records, hiddenCount } = selectTestRecords(d, ls);
  // A block whose filter hid EVERY record still renders: the header plus the
  // overflow row. Returning [] here would leave a person with eleven tests
  // looking like a person with none, which is the same clinical mis-reading the
  // "+ N more" row exists to prevent.
  if (!records.length && !hiddenCount) return [];
  const lines = [];
  lines.push({ runs: [{ text: 'Tests:', key: null }], italic: false });
  for (const t of records) {
    const it = composeTestRecord(t, ls);
    // One row per test — its name, genes, result and variants stay together on
    // that row as styled runs (TGCLOUD-1781).
    if (it.segments.length) {
      const runs = it.segments.filter((s) => String(s?.text ?? '') !== '');
      if (runs.length) lines.push({ runs, italic: false });
    }
    // The test's own comment keeps its own row, italic, as before.
    for (const c of singleLineOf(it.comment)) {
      lines.push({ runs: [{ text: c, key: 'pedigreeText' }], italic: true });
    }
  }
  const overflow = overflowBlockLine(hiddenCount);
  if (overflow) lines.push(overflow);
  return lines;
}

// Whether a stacked block should render: a custom `enabled(ls)` predicate when
// present (the composed Tests block is on if ANY of its child toggles is set),
// otherwise the single `key` toggle.
export function blockEnabled(blk, ls) {
  return blk.enabled ? blk.enabled(ls) : !!ls?.[blk.key];
}

// The value the per-unit filter JUDGES a consultation-date row by.
//
// A consultation date reaches a label ALREADY FORMATTED for display —
// PedigreeChart builds every member record through `formatConsultationDates`,
// which turns the server's ISO instants into the tenant's own moment tokens. So
// the row this block holds is "03/06/2026" under the default DD/MM/YYYY, or
// "03.06.2026" under DD.MM.YYYY, and handing THAT to the filter's date reader is
// what made the `within` control almost inert: `parsePartialDateString` only
// recognises YYYY[-MM[-DD]], and its fallback `new Date(str)` reads a slashed
// date as MM/DD — so a genuine DD/MM date with a day past the 12th is an Invalid
// Date, scores no usable date, and rule 1 (a record that cannot answer a filter
// is kept) keeps it. Roughly two thirds of a UK tenant's consultations therefore
// survived every recency window, silently.
//
// The window compares YEARS (`withinCutoffYear` in labelValueFilter.js), so the
// year is the whole of what the filter needs — and every format the Localisation
// setting offers prints it as the one four-digit group in the string, wherever
// it sits. Lifting it with a regex is therefore format-agnostic, and it builds
// no `Date` at all, so the verdict is identical at every UTC offset — unlike
// `new Date('YYYY-MM-DD')` or `toISOString().slice(0, 10)`, which are the two
// mirror-image traps this codebase keeps re-learning (TGCLOUD-1631/1707).
//
// A value with no four-digit year — a two-digit-year format, or the free text
// `formatConsultationDates` passes through when moment cannot read an entry —
// yields '' and is KEPT, which is rule 1 again.
//
// If the raw ISO value is ever carried alongside the printed one, this is the
// one function to change: it is the only place the block decides what a row's
// date IS.
const FOUR_DIGIT_YEAR = /(?:^|\D)(\d{4})(?:\D|$)/;
export const consultationDateFilterValue = (text) => {
  const match = FOUR_DIGIT_YEAR.exec(String(text ?? ''));
  return match ? match[1] : '';
};

// Recover the ROWS behind the judged values the filter kept.
//
// `filterLabelValues` returns a SUBSEQUENCE of what it was given — it filters,
// then slices — so walking both lists forward once, never backwards, maps each
// kept value to the row it came from. That is what makes it correct when two
// consultations fall in the same year and their judged values are identical: the
// pointer has already moved past the first row by the time the second is
// matched, so the two rows are never confused for each other.
const rowsForKeptValues = (rows, judged, keptJudged) => {
  const out = [];
  let i = 0;
  for (const value of keptJudged) {
    while (i < judged.length && judged[i] !== value) i += 1;
    if (i >= judged.length) break; // not a subsequence — cannot happen, fail safe
    out.push(rows[i]);
    i += 1;
  }
  return out;
};

// A block's lines as { runs:[{text,key}], italic } objects — the single shape
// both getLabelPos (reservation, uses .length) and Canvas (render) consume, so
// their row counts and the vertical layout stay in lockstep. A block with a
// custom `buildLines` (the composed Tests block) controls its own runs/italics;
// otherwise it is the plain "<header> + one row per field entry" stack, all
// upright, every row a single run styled by the block's own key.
//
// No `maxChars`: since TGCLOUD-1781 a record occupies one row whatever the gap,
// so there is no budget to thread. Callers still pass it — harmlessly — because
// the same argument list also reaches `estimateNoteLines` for the Person Note.
export function blockLines(blk, d, ls) {
  if (!blockEnabled(blk, ls)) return [];
  if (blk.buildLines) return blk.buildLines(d, ls);
  // The preset's per-unit filter, applied HERE so the reservation
  // (computeLabelLayout counts these lines) and the Canvas renderer (which draws
  // them) see one row list. TGCLOUD-1792 offered `limit` and `within` for the
  // `consultationDate` unit and stored them, but nothing applied them: this
  // block was built straight off the member field.
  //
  // A consultation date is a bare display string, not a record object, so there
  // is nothing to check `displayOnPedigree` on — `labelBlockItems` already drops
  // the blank entries, which is this unit's whole notion of visibility. The list
  // is in entry order, which is the only order these units offer
  // (SORT_KEYS_FOR_UNIT lists no key for them), so it is already the
  // "already-sorted" list `filterLabelValues` documents wanting.
  //
  // A unit with no filter — `confirmedDiagnosis` — passes through untouched and
  // with hiddenCount 0, because `filterLabelValues` short-circuits on
  // `hasValueFilter`.
  //
  // `filterValue` exists because a consultation-date row is not the value the
  // filter should judge: it is that value already printed in the tenant's date
  // format. The block judges on the year lifted back out of it, then maps the
  // survivors to the rows they came from, so the text drawn is still exactly the
  // text the member record holds. See consultationDateFilterValue.
  const rows = labelBlockItems(d, blk.field);
  const judged = blk.filterValue ? rows.map(blk.filterValue) : rows;
  const { items: kept, hiddenCount } = filterLabelValues(blk.key, judged, ls);
  const items = blk.filterValue ? rowsForKeptValues(rows, judged, kept) : kept;
  // As in buildTestBlockLines: a filter that hid everything still prints the
  // header and the overflow row rather than blanking the block.
  if (!items.length && !hiddenCount) return [];
  const lines = buildLabelBlockLines(blk.header, items).map((text) => ({
    runs: blk.rowRuns ? blk.rowRuns(text, blk.key) : [{ text, key: blk.key }],
    italic: false
  }));
  const overflow = overflowBlockLine(hiddenCount);
  if (overflow) lines.push(overflow);
  return lines;
}

// Single source of truth for the header + data of every label that renders as a
// stacked header + item list. Shared by getLabelPos and Canvas. Most blocks map
// a single toggle `key` to a member `field`; the Tests block is composed from
// several child toggles (including the italic Test Comment) via `enabled` +
// `buildLines`.
export const STACKED_LABEL_BLOCKS = [
  {
    key: 'consultationDate',
    header: 'Consultation Date:',
    field: 'consultationDate',
    yKey: 'consultationDate_y',
    // The row is a date PRINTED in the tenant's format; the filter has to judge
    // it on the year inside it, not on the string.
    filterValue: consultationDateFilterValue,
    rowRuns: consultationRowRuns
  },
  {
    key: 'confirmedDiagnosis',
    header: 'Diagnosis:',
    field: 'confirmedDiagnosis',
    yKey: 'confirmedDiagnosis_y'
  },
  {
    key: 'tests',
    header: 'Tests:',
    yKey: 'test_y',
    enabled: (ls) =>
      !!(
        ls?.tests ||
        ls?.genesTested ||
        ls?.variants ||
        ls?.testResults ||
        ls?.pedigreeText
      ),
    buildLines: buildTestBlockLines
  }
];
